/* eslint-disable no-nested-ternary */
import React, { useCallback , useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import withObservables from '@nozbe/with-observables'
import moment from 'moment/moment'
import PropTypes from 'prop-types'
import { v4 as uuidv4 } from 'uuid'

import filter from 'lodash/filter'
import find from 'lodash/find'
import first from 'lodash/first'
import includes from 'lodash/includes'
import initial from 'lodash/initial'
import isEmpty from 'lodash/isEmpty'
import isNaN from 'lodash/isNaN'
import isNil from 'lodash/isNil'
import map from 'lodash/map'
import max from 'lodash/max'
import orderBy from 'lodash/orderBy'
import replace from 'lodash/replace'
import split from 'lodash/split'
import times from 'lodash/times'
import toNumber from 'lodash/toNumber'

import { animalService } from '@smartcoop/database/services/animalService'
import { productGroupService } from '@smartcoop/database/services/productGroupService'
import { productService } from '@smartcoop/database/services/productService'
import { database } from '@smartcoop/database/web-database'
import { useDialog } from '@smartcoop/dialog'
import medicationControlSchema from '@smartcoop/forms/schemas/dairyFarm/medicationControl.schema'
import I18n, { useT } from '@smartcoop/i18n'
import { magnifier ,
  trash
} from '@smartcoop/icons'
import { getAnimalsDiseases } from '@smartcoop/services/apis/smartcoopApi/resources/herdsManagement'
import { getLots } from '@smartcoop/services/apis/smartcoopApi/resources/lot'
import { selectModuleIsTechnical } from '@smartcoop/stores/module/selectorModule'
import { selectFamilyGroupAccess, selectFamilyGroupFinancialDataAccess } from '@smartcoop/stores/property/selectorProperty'
import { SyncActions } from '@smartcoop/stores/sync'
import { selectProductsUpdatedAt } from '@smartcoop/stores/sync/selectorSync'
import { selectCurrentOwnerExtraAttributes, selectTechnicalFinancialDataAccess } from '@smartcoop/stores/technical/selectorTechnical'
import { selectUserExtraAttributes , selectUserCanWrite } from '@smartcoop/stores/user/selectorUser'
import { colors } from '@smartcoop/styles'
import { momentBackDateFormat, momentBackDateTimeFormat } from '@smartcoop/utils/dates'
import { convertValues, getBaseVolume, getExtenseBaseVolume } from '@smartcoop/utils/functions'
import Button from '@smartcoop/web-components/Button'
import CheckboxButton from '@smartcoop/web-components/CheckboxGroup/CheckboxButton'
import Form from '@smartcoop/web-components/Form'
import Icon from '@smartcoop/web-components/Icon'
import InputDate from '@smartcoop/web-components/InputDate'
import InputFloat from '@smartcoop/web-components/InputFloat'
import InputSelect from '@smartcoop/web-components/InputSelect'
import InputText from '@smartcoop/web-components/InputText'
import InputUnit from '@smartcoop/web-components/InputUnit'
import CheckboxesModal from '@smartcoop/web-containers/modals/CheckboxesModal'


import {
  Container,
  Row,
  ButtonsContainer,
  Divider
} from './styles'

const MedicationControlForm = (props) => {
  const {
    loading,
    onSubmit,
    setIsLoading,
    onCancel,
    defaultValues: externalDefaultValues,
    animalId,
    animalsList,
    products,
    productGroups,
    formRef,
    propertyId,
    productsLoading
  } = props

  const t = useT()
  const { createDialog } = useDialog()

  const dispatch = useDispatch()

  const userWrite = useSelector(selectUserCanWrite)
  const productsUpdatedAt = useSelector(selectProductsUpdatedAt)

  const userExtraAttributes = useSelector(selectUserExtraAttributes)
  const currentOwnerExtraAttributes = useSelector(selectCurrentOwnerExtraAttributes)

  const technicalFinancialDataAccess = useSelector(selectTechnicalFinancialDataAccess)
  const familyGroupFinancialDataAccess = useSelector(selectFamilyGroupFinancialDataAccess)

  const familyGroupAccess = useSelector(selectFamilyGroupAccess)
  const isTechnical = useSelector(selectModuleIsTechnical)

  const extraAttributes = useMemo(() => userExtraAttributes || {}, [userExtraAttributes])

  // eslint-disable-next-line no-nested-ternary
  const shouldDisplayCosts = useMemo(() => familyGroupAccess ? familyGroupFinancialDataAccess : isTechnical ? technicalFinancialDataAccess && currentOwnerExtraAttributes?.enabledCosts : extraAttributes?.enabledCosts,[currentOwnerExtraAttributes, extraAttributes, familyGroupAccess, familyGroupFinancialDataAccess, isTechnical, technicalFinancialDataAccess])

  const [defaultValues, setDefaultValues] = useState(externalDefaultValues || null)
  const [lotOptions, setLotOptions] = useState([])
  const [diseasesOptions, setDiseasesOptions] = useState([])
  const [blockedReason, setBlockedReason] = useState(false)
  const [animals, setAnimals] = useState([animalId || defaultValues?.animal?.id])
  const [carencyTime, setCarencyTime] = useState(defaultValues?.carencyTime ? [defaultValues?.carencyTime] : !isEmpty(defaultValues?.formFields) ? map(defaultValues?.formFields, item => item?.carencyTime) : ['0'])
  const [medicationStartDate, setMedicationStartDate] = useState(!isEmpty(defaultValues?.formFields) ? map(defaultValues?.formFields, item => item?.medicationStartDate) : defaultValues?.medicationStartDate ? [defaultValues?.medicationStartDate] : [moment().format(momentBackDateFormat)])
  const [treatmentTime, setTreatmentTime] = useState(defaultValues?.treatmentTime ? [defaultValues?.treatmentTime] : !isEmpty(defaultValues?.formFields) ? map(defaultValues?.formFields, item => toNumber(isEmpty(item?.treatmentTime) ? '0' : item?.treatmentTime)) : [''])
  const [type, setType] = useState(defaultValues?.type)
  const [checkbox, setCheckbox] = useState(defaultValues?.type ? defaultValues?.type === 'calendario-de-vacinacao' || defaultValues?.type === 'hormonios-estimulantes-suplementos'  ? map(defaultValues?.formFields, item => !isEmpty(item?.medicationEndDate)) :  map(defaultValues?.formFields, () => false) : [false])
  const [lastManualMedicationEndDate, setLastManualMedicationEndDate] = useState(defaultValues?.medicationEndDate)
  const [items, setItems] = useState(!isEmpty(defaultValues?.formFields) ? times(defaultValues?.formFields.length, () => uuidv4()) : [uuidv4()])
  const [blockOnChange, setBlockOnChange] = useState(false)
  const [resetBlock, setResetBlock] = useState(false)
  const [values, setValues] = useState(!isEmpty(defaultValues?.formFields) ? map(defaultValues?.formFields, (item) => ({ value: item?.value || '', valueHA: item?.valueHA || '', valueDose: item?.valueDose || '' })) : [{ value: '', valueHA: '', valueDose: '' }])
  const [dose, setDose] = useState(!isEmpty(defaultValues?.formFields) ? map(defaultValues?.formFields, (item) => ({ value: item?.dose ?? '', doseUnit: item?.doseUnit ?? '' })) : [{ value: '', doseUnit: '' }])

  const productOptions = useMemo(() => {
    const productGroup = find(productGroups, item => item?.slug === 'produtos-veterinarios')
    const filteredProducts = filter(products, item => item?.productGroupId === productGroup?.productGroupId)

    return map(filteredProducts, item => ({
      label: item?.shortDescription,
      value: item?.productId,
      productGroup
    }))
  }, [productGroups, products])

  const activeAnimals = useMemo(() => {
    const current = filter(animalsList, item => item?.active && !item?.discard)
    const animal = find(animalsList, item => item?.animalId === animalId)
    if(animal?.discard) {
      current.push(animal)
    }
    return current
  }, [animalId, animalsList])

  const medicationEndDate = useMemo(() => type === 'secagem' ? map(medicationStartDate, item => moment(item, momentBackDateFormat).format(momentBackDateFormat)) : map(medicationStartDate, (item, index) => moment(item, momentBackDateFormat).add(treatmentTime?.[index] ? toNumber(treatmentTime?.[index]) : 0, 'days').format(momentBackDateFormat)),[type, medicationStartDate, treatmentTime])

  const carencyLiberationDate = useMemo(() => map(medicationEndDate, (item, index) => moment(item, momentBackDateFormat).add(carencyTime?.[index] ? toNumber(carencyTime?.[index]) : 0, 'days'))
    ,[medicationEndDate, carencyTime])

  const lastStart = useMemo(() => moment(max(map(medicationStartDate, d => moment(d).valueOf()))).format(momentBackDateFormat),[medicationStartDate])

  const maxTreatmentTime = useMemo(() => max(treatmentTime),[treatmentTime])

  const maxCarencyTime = useMemo(() => max(carencyTime),[carencyTime])

  const lastMedicationEndDate = useMemo(() => !isEmpty(lastStart) ? type === 'secagem' ? moment(lastStart, momentBackDateFormat).format() : moment(lastStart, momentBackDateFormat).add(maxTreatmentTime ? toNumber(maxTreatmentTime) : 0, 'days').format(momentBackDateFormat) : '',[lastStart, type, maxTreatmentTime])

  const lastCarencyLiberationDate = useMemo(() => moment(lastMedicationEndDate, momentBackDateFormat).add(maxCarencyTime ? toNumber(maxCarencyTime) : 0, 'days')
    ,[lastMedicationEndDate, maxCarencyTime])

  const schemaProps = useMemo(() => ({
    realizado: checkbox
  }), [checkbox])

  const handleSubmit = useCallback(
    (data) => {
      const updatedData = {
        ...data,
        registerCode: defaultValues?.registerCode,
        propertyId,
        // eslint-disable-next-line no-nested-ternary
        medicationEndDate: type !== 'calendario-de-vacinacao' ? lastMedicationEndDate : (checkbox ? lastManualMedicationEndDate : null),
        ccsPrepartum: toNumber(data?.ccsPrepartum),
        ccsPostpartum: toNumber(data?.ccsPostpartum),
        formFields: map(data.formFields, (item, key) => ({
          ...item,
          medicationEndDate: type !== 'calendario-de-vacinacao' && checkbox?.[key] ? medicationEndDate?.[key] : (checkbox?.[key] ? lastManualMedicationEndDate : null),
          value: values[key]?.value,
          valueDose: values[key]?.valueDose,
          product: find(productOptions, prod => prod?.value === item?.productId)?.label,
          referenceId: defaultValues?.formFields?.[key]?.referenceId || uuidv4()
        }))
      }
      Object.keys(updatedData).forEach(key => {
        if (updatedData[key] === '') {
          delete updatedData[key]
        }
      })

      onSubmit(updatedData)
    },
    [checkbox, defaultValues, lastManualMedicationEndDate, lastMedicationEndDate, medicationEndDate, onSubmit, productOptions, propertyId, type, values]
  )

  const loadLots = useCallback(async () => {
    const { data: { data } } = await getLots({}, { propertyId })

    setLotOptions(map(data, item => ({
      label: item.name,
      value: item.id
    })))
  },[propertyId])

  const loadDiseases = useCallback(async () => {
    setBlockedReason(true)
    const { data: { data } } = await getAnimalsDiseases({ type, limit: 99999 })

    setDiseasesOptions(map(data, item => ({
      label: item.name,
      value: item.id
    })))
    setBlockedReason(false)
  },[type])

  const onChangeLots = useCallback((lots) => {
    setIsLoading(true)
    const updatedAnimalsFromLots = []
    const func = map(lots, (lot) => {
      const newAnimalsId = map(filter(activeAnimals, (item) => item?.lotId === lot), (item) => item?.animalId)
      updatedAnimalsFromLots.push(...newAnimalsId)
    })
    Promise.all(func).then(() => {
      const currentAnimals = [...animals]
      const oldAndNotNew = filter(currentAnimals, (item) => !includes(updatedAnimalsFromLots, item))
      const updatedAnimals = [...oldAndNotNew, ...updatedAnimalsFromLots]
      formRef.current.getFieldRef('animalIds').setValue(updatedAnimals)
      setAnimals(updatedAnimals)
    })
    setIsLoading(false)
  },[animals, activeAnimals, formRef, setIsLoading])


  useEffect(() => {
    loadLots()
    loadDiseases()
  },[loadDiseases, loadLots])

  const typeOptions = [
    {
      label: 'Calendário de vacinação',
      value: 'calendario-de-vacinacao'
    },
    {
      label: 'Diagnóstico de doença',
      value: 'diagnostico-de-doenca'
    },
    {
      label: 'Secagem',
      value: 'secagem'
    },
    {
      label: 'Hormônios, Estimulantes e Suplementos',
      value: 'hormonios-estimulantes-suplementos'
    },
    {
      label: 'Outras Vacinas Preventivas',
      value: 'outras-vacinas-preventivas'
    }
  ]

  const doseUnitOptions = [
    {
      label: 'ml',
      value: 'ml'
    },
    {
      label: 'mg',
      value: 'mg'
    },
    {
      label: 'g',
      value: 'g'
    }
  ]

  const applicationModeOptions = [
    {
      label: 'Injetável',
      value: 'Injetável'
    },
    {
      label: 'Intramamário',
      value: 'Intramamário'
    },
    {
      label: 'Tópico',
      value: 'Tópico'
    },
    {
      label: 'Oral',
      value: 'Oral'
    }
  ]

  const treatmentInterval = [
    {
      label: 'Dose única',
      value: 'Dose única'
    },
    {
      label: 'Diária',
      value: 'Diária'
    },
    {
      label: '4 Horas',
      value: '4 Horas'
    },
    {
      label: '6 Horas',
      value: '6 Horas'
    },
    {
      label: '8 Horas',
      value: '8 Horas'
    },
    {
      label: '12 Horas',
      value: '12 Horas'
    },
    {
      label: '7 Dias',
      value: '7 Dias'
    },
    {
      label: '14 Dias',
      value: '14 Dias'
    },
    {
      label: '21 Dias',
      value: '21 Dias'
    }
  ]


  useEffect(() => {
    if(type === 'secagem' && !isEmpty(diseasesOptions)) {
      formRef.current.setData({ animalDiseaseIds: [diseasesOptions[0]?.value] })
      formRef.current.setData({ applicationMode: 'Intramamário' })
      formRef.current.setData({ treatmentInterval: 'Dose única' })
      formRef.current.setData({ treatmentTime: '0' })
    }
  },[type, diseasesOptions, formRef])

  const loadProducts = useCallback(
    () => {
      if(!productsLoading) {
        dispatch(SyncActions.setProductsLoading(true))
        productService(database).syncProducts({ updatedAt: productsUpdatedAt, digitalization: true }, () => {
          dispatch(SyncActions.setProductsLoading(false))
        })
        const newDate = moment().subtract(4, 'minutes').format(momentBackDateTimeFormat)
        dispatch(SyncActions.setProductsUpdatedAt(newDate))
      }
    },
    [dispatch, productsLoading, productsUpdatedAt]
  )

  useEffect(() => {
    loadProducts()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  const removeItem = useCallback((position) => {
    setBlockOnChange(true)
    const data = formRef.current.getData()

    setDefaultValues(null)

    const updatedFields = filter(data.formFields, (_, i) => i !== position)

    updatedFields.forEach((item, index) => {
      formRef.current.getFieldRef(`formFields[${ index }].dose`).setValue(item?.dose || '')
      formRef.current.getFieldRef(`formFields[${ index }].productId`).setValue(item?.productId || '')
      formRef.current.getFieldRef(`formFields[${ index }].doseUnit`).setValue(item?.doseUnit || '')
      formRef.current.getFieldRef(`formFields[${ index }].applicationMode`).setValue(item?.applicationMode || '')
      formRef.current.getFieldRef(`formFields[${ index }].medicationStartDate`).setValue(item?.medicationStartDate || '')
      formRef.current.getFieldRef(`formFields[${ index }].treatmentInterval`).setValue(item?.treatmentInterval || '')
      formRef.current.getFieldRef(`formFields[${ index }].treatmentTime`).setValue(item?.treatmentTime || '')
      formRef.current.getFieldRef(`formFields[${ index }].carencyTime`).setValue(item?.carencyTime || '')
    })

    formRef.current.clearField(`formFields[${ position }]`)

    setCarencyTime(old => filter(old, (_, i) => i !== position))
    setMedicationStartDate(old => filter(old, (_, i) => i !== position))
    setTreatmentTime(old => filter(old, (_, i) => i !== position))
    setValues((old) => filter(old, (_, i) => i !== position))
    setCheckbox((old) => filter(old, (_, i) => i !== position))

    const updatedDose = {}
    updatedFields.forEach((item, index) => {
      updatedDose[index] = {
        value: item?.dose ?? '',
        doseUnit: item?.doseUnit ?? ''
      }
    })

    setDose(updatedDose)
    setItems((old) => filter(old, (_, key) => key !== position))
    setTimeout(() => {
      setResetBlock((old) => !old)
    }, 200)
  }, [formRef])

  useEffect(() => {
    setBlockOnChange(false)
  }, [resetBlock])

  const calculateQuantity = useCallback((interval, quantity, time) => {

    const intervalMultipliers = {
      'Diária': () => quantity * time,
      '4 Horas': () => quantity * ((time * 24) / 4),
      '6 Horas': () => quantity * ((time * 24) / 6),
      '8 Horas': () => quantity * ((time * 24) / 8),
      '12 Horas': () => quantity * ((time * 24) / 12),
      '7 Dias': () => quantity + (quantity * Math.floor(time / 7)),
      '14 Dias': () => quantity + (quantity * Math.floor(time / 14)),
      '21 Dias': () => quantity + (quantity * Math.floor(time / 21))
    }

    const calculate = intervalMultipliers[interval]

    return calculate ? calculate() : quantity
  }, [])

  const updateCostValues = useCallback((value, item, from) => {
    if (!shouldDisplayCosts || blockOnChange) return
    setBlockOnChange(true)
    const formUnit = formRef.current.getFieldValue(`formFields[${ item }].doseUnit`)
    const formDose = from === 'formDose' ? value : formRef.current.getFieldValue(`formFields[${ item }].dose`)
    const formTreatmentInterval = from === 'treatmentInterval' ? value : formRef.current.getFieldValue(`formFields[${ item }].treatmentInterval`)
    const formTreatmentTime = formRef.current.getFieldValue(`formFields[${ item }].treatmentTime`)
    const formType = formRef.current.getFieldValue('type')
    const quantity = calculateQuantity(formTreatmentInterval, toNumber(formDose || '0'), formType === 'calendario-de-vacinacao' ? 1 : toNumber(formTreatmentTime || '0'))

    if (isEmpty(formUnit) || isEmpty(toString(quantity))) {
      return
    }

    const unit = first(split(formUnit, '/'))
    const convertedCost = convertValues(quantity, unit, getBaseVolume(unit))

    switch (from) {
      case 'dose':
        setValues((old) => map(items, (_, index) => index === item ? ({ valueDose: value, value: value * convertedCost }) : old?.[index] ))
        break
      case 'unit':
      case 'formDose':
      case 'treatmentInterval':
      case 'treatmentTime':
        setValues((old) => map(items, (_, index) => index === item ? ({ valueDose: old?.[index]?.valueDose, value: old?.[index]?.valueDose * convertedCost }) : old?.[index] ))
        break
      case 'total':
        setValues((old) => map(items, (_, index) => index === item ? ({ valueDose:value / convertedCost, value }) : old?.[index] ))
        break
      default:
        break
    }
    setResetBlock((old) => !old)
  }, [blockOnChange, calculateQuantity, formRef, items, shouldDisplayCosts])

  const addNewRow = useCallback(() => {
    if(items.length > 1) {
      const newMedicationStartDate = formRef.current.getFieldValue(`formFields[${ items.length - 2 }].medicationStartDate`)
      if(isEmpty(formRef.current.getFieldValue(`formFields[${ items.length - 1 }].medicationStartDate`)) && !isEmpty(newMedicationStartDate)) {
        formRef.current.getFieldRef(`formFields[${ items.length - 1 }].medicationStartDate`).setValue(newMedicationStartDate)
      }
    }

    if(formRef.current.getFieldRef(`formFields[${ items.length - 1 }].applicationMode`)
      && formRef.current.getFieldRef(`formFields[${ items.length - 1 }].treatmentInterval`)
      && formRef.current.getFieldRef(`formFields[${ items.length - 1 }].treatmentTime`)) {

      const emptyApplicationMode = isEmpty(formRef.current.getFieldValue(`formFields[${ items.length - 1 }].applicationMode`))
      const emptyTreatmentInterval = isEmpty(formRef.current.getFieldValue(`formFields[${ items.length - 1 }].treatmentInterval`))
      const emptyTreatmentTime = isEmpty(formRef.current.getFieldValue(`formFields[${ items.length - 1 }].treatmentTime`))
      const emptyAnimalDiseaseIds = isEmpty(formRef.current.getFieldValue('animalDiseaseIds'))

      if(type === 'calendario-de-vacinacao' && emptyApplicationMode && emptyTreatmentInterval && emptyTreatmentTime) {
        formRef.current.getFieldRef(`formFields[${ items.length - 1 }].applicationMode`).setValue('Injetável')
        formRef.current.getFieldRef(`formFields[${ items.length - 1 }].treatmentInterval`).setValue('Dose única')
        formRef.current.getFieldRef(`formFields[${ items.length - 1 }].treatmentTime`).setValue('0')
      }
      if(type === 'secagem' && !isEmpty(diseasesOptions) && emptyApplicationMode && emptyTreatmentInterval && emptyTreatmentTime) {
        if(emptyAnimalDiseaseIds) {
          formRef.current.getFieldRef('animalDiseaseIds').setValue([diseasesOptions[0]?.value])
        }
        formRef.current.getFieldRef(`formFields[${ items.length - 1 }].applicationMode`).setValue('Intramamário')
        formRef.current.getFieldRef(`formFields[${ items.length - 1 }].treatmentInterval`).setValue('Dose única')
        formRef.current.getFieldRef(`formFields[${ items.length - 1 }].treatmentTime`).setValue('0')
      }
    }

  },[diseasesOptions, formRef, items, type])

  useEffect(() => {
    addNewRow()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items])

  return (
    <Form
      style={ { display: 'flex', flexDirection: 'column', width: '100%' } }
      ref={ formRef }
      schemaConstructor={ medicationControlSchema }
      schemaProps={ schemaProps }
      onSubmit={ handleSubmit }
    >
      <Container>
        <Row>
          <InputSelect
            onChange={ ({ target: { value } }) => setAnimals(value) }
            label={ t('earrings') }
            name="animalIds"
            options={ orderBy(map(activeAnimals, item => ({ label: item?.earringCode, value: item?.animalId })), item => isNaN(toNumber(item?.label)) ? item?.label : toNumber(item?.label)) }
            multiple
            defaultValue={ [animalId || defaultValues?.animal?.id] }
            checkBoxSelectAll
            order={ false }
          />
          <Button
            id="search-lot-button"
            onClick={ () => createDialog(
              {
                id: 'select-lots-modal',
                Component: CheckboxesModal,
                props: {
                  items: lotOptions,
                  title: t('lot', { howMany: 2 }),
                  onConfirm: (response) => {
                    onChangeLots(response)
                  }
                }
              }
            ) }
            variant="outlined"
            style={ { marginBottom: '15px' } }
            disabled={ isEmpty(activeAnimals) }
          >
            <Icon
              icon={ magnifier }
              style={ { color: '#000' } }
            />
          </Button>
        </Row>
        <InputSelect
          label={ t('type') }
          name="type"
          options={ typeOptions }
          onChange={ ({ target: { value } }) => setType(value) }
          defaultValue={ defaultValues?.type }
        />
        <InputSelect
          label={ t('reason ') }
          name="animalDiseaseIds"
          options={ diseasesOptions }
          multiple
          loading={ blockedReason }
          disabledWhenLoading
          disabled={ isEmpty(type) }
          defaultValue={ !isEmpty(defaultValues?.animalDiseases) ? map(defaultValues?.animalDiseases, item => item.id) : [] }
        />

        { map(items, (id, item) => (
          <div key={ `${ id }-${ type }` }>
            {item === 0 ? (
              <></>
            ) : (
              <Divider />
            )}
            <div style={ { display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' } }>
              <Button
                id="button-delete-item"
                onClick={ () => removeItem(item) }
                variant="outlined"
                disabled={ items?.length === 1 }
                style={ { maxWidth: '30px' } }
              >
                <Icon icon={ trash } color={ items?.length === 1 ? colors.muted : colors.red } size={ 16 } />
              </Button>
            </div>

            <InputDate
              label={ type === 'calendario-de-vacinacao' ? t('predicted date') : t('treatment start date') }
              name={ `formFields[${ item }].medicationStartDate` }
              onChange={ (value) => {
                setMedicationStartDate(old => map(items, (_, index) => index === item ? value : old?.[index]))
              } }
              defaultValue={ isEmpty(defaultValues) ? moment().format(momentBackDateFormat) : defaultValues?.formFields?.[item]?.medicationStartDate }
              fullWidth
              pickerProps={ { fullWidth: true } }
            />

            <InputSelect
              label={ t('products') }
              name={ `formFields[${ item }].productId` }
              options={ productOptions }
              defaultValue={ defaultValues?.formFields?.[item]?.productId || '' }
              externalLoading={ productsLoading }
            />

            <div style={ { display: 'flex', flex: 1 } }>
              <InputFloat
                name={ `formFields[${ item }].dose` }
                label={ t('dose') }
                maxLength={ 8 }
                defaultValue={ !isNil(defaultValues?.formFields) ? toNumber(replace(defaultValues?.formFields?.[item]?.dose, ',', '.')) || 0 : 0 }
                fullWidth
                style={ { marginRight: '5px' } }
                onChange={ ({ target: { value } }) => {
                  setDose(old => {
                    const currentDose = { ...old }
                    currentDose[item] = old?.[item] || {}
                    currentDose[item].value = value
                    updateCostValues(null, item, 'formDose')
                    return currentDose
                  })
                }
                }
              />
              <InputSelect
                label={ t('unit') }
                name={ `formFields[${ item }].doseUnit` }
                options={ doseUnitOptions }
                defaultValue={ !isNil(defaultValues?.formFields) ? defaultValues?.formFields?.[item]?.doseUnit : '' }
                fullWidth
                style={ { marginLeft: '5px' } }
                onChange={ ({ target: { value } }) => {
                  setDose(old => {
                    const currentDose = { ...old }
                    currentDose[item] = old?.[item] || {}
                    currentDose[item].doseUnit = value

                    updateCostValues(null, item, 'unit')
                    return currentDose
                  })
                }
                }
              />
            </div>
            {shouldDisplayCosts ? (
              <div style={ { display: 'flex', flexDirection: 'row' } }>
                <InputUnit
                  key={ `valueDose-${ item }` }
                  detached
                  label={ `${ t('cost by') } ${ getExtenseBaseVolume(dose?.[item]?.doseUnit || defaultValues?.formFields?.[item]?.doseUnit) || 'dose' }` }
                  unit={ `R$/${ getExtenseBaseVolume(dose?.[item]?.doseUnit || defaultValues?.formFields?.[item]?.doseUnit) || 'dose' }` }
                  type="float"
                  fullWidth
                  onChange={ ({ target: { value } }) => updateCostValues(value, item, 'dose') }
                  disabled={ !dose?.[item]?.doseUnit || !dose?.[item]?.value }
                  value={ values[item]?.valueDose }
                  style={ { marginRight: 10 } }
                />

                <InputUnit
                  key={ `value-${ item }` }
                  disabled={ !dose?.[item]?.doseUnit || !dose?.[item]?.value }
                  detached
                  label={ t('total cost') }
                  unit="R$"
                  type="float"
                  fullWidth
                  onChange={ ({ target: { value } }) => updateCostValues(value, item, 'total') }
                  value={ values[item]?.value }
                />
              </div>
            ) : null }



            {type === 'calendario-de-vacinacao' ? (
              <CheckboxButton
                label={ `${ t('held') }?` }
                onChange={ () => setCheckbox(old => map(old, (each, pos) => pos === item ? !each : each)) }
                checked={ checkbox?.[item] }
                value={ checkbox?.[item] }
                style={ { width: '100%' } }
              />
            ) : null}
            <Row>
              <InputSelect
                key={ `treatmentInterval-${ id }-${ type }` }
                label={ t('treatment interval') }
                name={ `formFields[${ item }].treatmentInterval` }
                defaultValue={ defaultValues?.formFields?.[item]?.treatmentInterval }
                options={ treatmentInterval }
                onChange={ ({ target: { value } }) => {
                  updateCostValues(value, item, 'treatmentInterval')
                } }
                style={ { width: '50%', marginRight: 5 } }
              />
              <InputUnit
                key={ `treatmentTime-${ id }-${ type }` }
                name={ `formFields[${ item }].treatmentTime` }
                label={ t('treatment time') }
                unit={ t('day', { howMany: 2 }) }
                defaultValue={ defaultValues?.formFields?.[item]?.treatmentTime }
                onChange={ ({ target: { value } }) => {
                  setTreatmentTime(old => {
                    const newData = [...old]
                    newData[item] = value
                    return newData
                  })
                  updateCostValues(null, item, 'treatmentTime')
                } }
                type="integer"
                style={
                  {
                    opacity: !(type === 'diagnostico-de-doenca' || type === 'outras-vacinas-preventivas') ? 0 : 1,
                    height: !(type === 'diagnostico-de-doenca' || type === 'outras-vacinas-preventivas') ? 0 : 'auto',
                    width: '50%',
                    marginLeft: 5
                  }
                }
              />
            </Row>
            <Row>
              <InputSelect
                label={ t('application mode') }
                name={ `formFields[${ item }].applicationMode` }
                defaultValue={ defaultValues?.formFields?.[item]?.applicationMode }
                options={ applicationModeOptions }
                style={ { width: '50%', marginRight: 5 } }
              />
              <InputUnit
                key={ `carencyTime-${ id }-${ type }` }
                name={ `formFields[${ item }].carencyTime` }
                onChange={ ({ target: { value } }) => {
                  setCarencyTime(old => {
                    const newData = [...old]
                    newData[item] = value
                    return newData
                  })
                } }
                label={ t('carency time') }
                unit={ t('day', { howMany: 2 }) }
                defaultValue={ defaultValues?.formFields?.[item]?.carencyTime || '0' }
                type="integer"
                style={ { width: '50%', marginLeft: 5 } }
              />
            </Row>
            <Row>
              <InputDate
                fullWidth
                detached
                disabled={ type !== 'calendario-de-vacinacao' }
                label={ type === 'calendario-de-vacinacao' ? t('realization date') : t('treatment end date') }
                style={
                  {
                    opacity: type === 'calendario-de-vacinacao' && !checkbox?.[item] ? 0 : 1,
                    height: type === 'calendario-de-vacinacao' && !checkbox?.[item] ? 0 : 'auto',
                    marginRight: '5px'
                  }
                }

                value={ medicationEndDate?.[item] || '' }
              />
              <InputDate
                detached
                fullWidth
                disabled
                label={ t('carency liberation date') }
                value={ carencyLiberationDate?.[item] || '' }
                style={ { marginLeft: 5 } }
              />
            </Row>
          </div>
        ))}
        <div style={ { display: 'flex', width: '100%', justifyContent: 'flex-end', marginBottom: '10px' } }>
          <div style={ { display: 'flex', flexDirection: 'row' } }>
            <Button
              id="button-remove-item"
              onClick={ () => {
                setCheckbox(old => initial(old))
                setItems((old) => initial(old))
                setValues((old) => initial(old))
              } }
              variant="outlined"
              disabled={ items.length === 1 }
            >
        -
            </Button>

            <Button
              id="button-add-item"
              onClick={ () => {
                setCheckbox(old => [...old, false])
                setItems((old) => [...old, uuidv4()])
                setValues((old) => [...old, { value: '', valueHA: '', valueDose: '' }])
                setCarencyTime((old) => [...old, '0'])
                setTreatmentTime((old) => [...old, ''])
              } }
              style={ { marginLeft: '20px' } }
            >
        +
            </Button>
          </div>
        </div>

        <Row>
          <InputDate
            detached
            fullWidth
            onChange={ (value) => setLastManualMedicationEndDate(value) }
            disabled={ type !== 'calendario-de-vacinacao' }
            label={ type === 'calendario-de-vacinacao' ? t('realization date') : t('treatment end date') }
            value={ type !== 'calendario-de-vacinacao' ? lastMedicationEndDate : lastManualMedicationEndDate }
            style={
              {
                opacity: type === 'calendario-de-vacinacao' && isEmpty(filter(checkbox, item => item)) ? 0 : 1,
                heigth: type === 'calendario-de-vacinacao' && isEmpty(filter(checkbox, item => item)) ? 0 : 'auto',
                marginRight: '20px'
              }
            }
          />
          <InputDate
            detached
            fullWidth
            disabled
            label={ t('carency liberation date') }
            value={ lastCarencyLiberationDate }
          />
        </Row>

        <Row style={ { opacity: type === 'secagem' ? 1 : 0, height: type === 'secagem' ? 'auto' : 0 } }>
          <InputUnit
            name="ccsPrepartum"
            label={ t('css before birth') }
            defaultValue={ defaultValues?.ccsPrepartum }
            unit="CSS"
            type="integer"
            style={ { marginRight: '20px' } }
          />
          <InputUnit
            name="ccsPostpartum"
            label={ t('css after birth') }
            defaultValue={ defaultValues?.ccsPostpartum }
            unit="CSS"
            type="integer"
          />
        </Row>
        <InputText
          label={ t('observations') }
          name="observation"
          multiple
          fullWidth
          defaultValue={ defaultValues?.observation }
        />
      </Container>

      <ButtonsContainer>
        <Button
          id="web-cancel-form-button"
          onClick={ onCancel }
          color={ colors.white }
          disabled={ loading }
          style={ { marginLeft: '10px', marginRight: '10px' } }
        >
          <I18n>cancel</I18n>
        </Button>
        <Button
          id="web-save-form-button"
          onClick={ () => formRef.current.submit() }
          disabled={ loading || !userWrite }
          style={ { marginLeft: '10px', marginRight: '10px' } }
        >
          <I18n>save</I18n>
        </Button>
      </ButtonsContainer>
    </Form>
  )
}

MedicationControlForm.propTypes = {
  loading: PropTypes.bool,
  animalId: PropTypes.string,
  propertyId: PropTypes.string,
  onSubmit: PropTypes.func,
  defaultValues: PropTypes.object,
  formRef: PropTypes.object.isRequired,
  setIsLoading: PropTypes.func,
  animalsList: PropTypes.array,
  productsLoading: PropTypes.bool.isRequired,
  products: PropTypes.array.isRequired,
  productGroups: PropTypes.array.isRequired,
  onCancel: PropTypes.func
}

MedicationControlForm.defaultProps = {
  loading: false,
  animalsList: [],
  animalId: null,
  propertyId: null,
  onSubmit: () => {},
  defaultValues: {},
  onCancel: () => {},
  setIsLoading: () => {}
}

const enhance = withObservables(['propertyId', 'productsLoading'], ({ propertyId, productsLoading }) => ({
  animalsList: animalService(database).observeAnimals(propertyId),
  products: productService(database).observeProducts(productsLoading),
  productGroups: productGroupService(database).observeProductGroups(productsLoading)
}))

const EnhancedMedicationControlForm = enhance(MedicationControlForm)

export default EnhancedMedicationControlForm

